/** Inline SVG charts — no external chart dependency, honest quality labels. */
import type { ReactNode } from "react";

export interface Point {
  t: string;
  v: number;
}

export function Sparkline({
  points,
  width = 720,
  height = 160,
  color = "#2563eb",
}: {
  points: Point[];
  width?: number;
  height?: number;
  color?: string;
}) {
  if (points.length === 0) {
    return (
      <div className="empty-chart">
        暂无数据 — 未接入/尚无事件时显示此状态,不画假 0
      </div>
    );
  }
  const pad = 24;
  const max = Math.max(...points.map((p) => p.v), 1);
  const step = (width - pad * 2) / Math.max(points.length - 1, 1);
  const xy = points.map((p, i) => [
    pad + i * step,
    height - pad - (p.v / max) * (height - pad * 2),
  ]);
  const path = xy.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x},${y}`).join(" ");
  const area = `${path} L${xy[xy.length - 1][0]},${height - pad} L${pad},${height - pad} Z`;
  return (
    <svg width="100%" viewBox={`0 0 ${width} ${height}`} role="img">
      <path d={area} fill={color} opacity="0.12" />
      <path d={path} fill="none" stroke={color} strokeWidth="2" />
      <text x={pad} y={16} fontSize="11" fill="#64748b">max {max}</text>
      <text x={pad} y={height - 6} fontSize="11" fill="#64748b">
        {new Date(points[0].t).toLocaleString()}
      </text>
      <text x={width - pad} y={height - 6} fontSize="11" fill="#64748b" textAnchor="end">
        {new Date(points[points.length - 1].t).toLocaleString()}
      </text>
    </svg>
  );
}

export function QualityBadge({ quality }: { quality: Record<string, unknown> }) {
  if (!quality) return null;
  const pending = Number(quality.pending_aggregation ?? 0);
  const provisional = Boolean(quality.provisional);
  const labels: ReactNode[] = [];
  if (pending > 0)
    labels.push(<span key="p" className="badge warn">待聚合 {pending}</span>);
  if (provisional)
    labels.push(<span key="pr" className="badge warn">provisional(96h 封桶前)</span>);
  if (labels.length === 0)
    labels.push(<span key="ok" className="badge ok">已处理至 {String(quality.processed_through ?? "—").slice(11, 19)}</span>);
  return <div className="quality">{labels}</div>;
}

export function BarList({
  items,
  overflow,
}: {
  items: { value: string; count: number }[];
  overflow?: boolean;
}) {
  if (items.length === 0) return <div className="empty-chart">无样本</div>;
  const max = Math.max(...items.map((i) => i.count), 1);
  return (
    <div className="barlist">
      {overflow && (
        <div className="badge warn" style={{ marginBottom: 8 }}>
          存在 __other_overflow__ 降级分组:榜单非全量精确 Top K
        </div>
      )}
      {items.map((i) => (
        <div className="barrow" key={i.value}>
          <span className="barlabel">{i.value}</span>
          <div className="bartrack">
            <div className="barfill" style={{ width: `${(i.count / max) * 100}%` }} />
          </div>
          <span className="barcount">{i.count.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}
